import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Provider } from '../../models/Provider';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { getFormattedAndValidatedPhoneNumber } from '../../utils/phone.utils';
import { adobeSearchResultClickEvent } from '../AdobeTagging/adobeSearchResultClickEvent';
import { mobileOnly, phoneOnly } from '../ConstantsStyles';
import { usePortalSignInData } from '../GuestHeader/utils';
import {
  convertProviderTypeToAdobeType,
  formatProviderId,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { CallModal } from './CallModal';
import { ResponsiveText } from './DataCard.style';

const PhoneTag = styled('a', {
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  '&:hover,&:active': {
    textDecoration: 'none',
  },
});

export const CallAndScheduleText = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  dynamic: ({ cssProps: { expandedProviderListView } = {} }) => ({
    '@media (max-width: 1247px)': {
      display: expandedProviderListView ? 'flex' : 'block',
      flexWrap: 'wrap',
      textAlign: 'end',
    },
  }),
});

export const CallButton = styled('button', {
  gap: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  dynamic: ({
    cssProps: {
      enableMapViewHideOption,
      expandedProviderListView,
      doShowDocASAP,
      isGroupedLocationDrawer = false,
    } = {},
  }) => ({
    ...(enableMapViewHideOption && {
      height: expandedProviderListView ? '40px' : '32px',
    }),
    width:
      (doShowDocASAP && !enableMapViewHideOption) || expandedProviderListView
        ? ''
        : 'auto',
    height: expandedProviderListView ? '40px' : '32px',
    gap: enableMapViewHideOption ? '6px' : '4px',
    padding:
      enableMapViewHideOption && !isGroupedLocationDrawer
        ? '1px 16px'
        : '4px 16px !important',
    border: enableMapViewHideOption ? '1px solid $core.color.brand.100' : '',
    maxWidth:
      (doShowDocASAP && !enableMapViewHideOption) || expandedProviderListView
        ? ''
        : '100%',
    borderRadius: enableMapViewHideOption ? '24px' : '',
    '@media (max-width: 984px)': {
      height: '32px',
    },
  }),
});

export const phoneTextStyles = (
  enableMapViewHideOption: boolean = false,
  isGroupedLocationDrawer: boolean = false
) => ({
  fontSize: enableMapViewHideOption ? '16px' : '14.22px !important',
  lineHeight: '16px',
  fontWeight: enableMapViewHideOption ? '700' : '600',
  padding: '0 4px !important',
  '@media (max-width: 464px)': {
    fontSize: '$extraSmallText !important',
    fontWeight: '700',
  },
  cursor: 'pointer',
  '&.abyss-text-root': {
    overflow: isGroupedLocationDrawer && 'visible',
  },
});

export const phoneTextStyleGroupLocationDrawer = {
  fontSize: '16px !important',
  lineHeight: '16px',
  fontWeight: '700',
  padding: '0 4px !important',
  cursor: 'pointer',
};

const callButtonIconColor = (enableMapViewHideOption) =>
  enableMapViewHideOption ? '$core.color.brand.100' : '$core.color.brand.80';

const callText = (enableMapViewHideOption, phone, phoneNumber, call) =>
  enableMapViewHideOption && phone ? phoneNumber : call;
type Props = {
  headers?: ResponseHeaders;
  id?: string;
  phone?: string;
  sectionType?: string;
  practitioner?: Provider;
  providerType?: string;
  locationForAnalytics?: string;
  popupContainer?: boolean;
  indexForAnalytics?: number;
  selectedFilters?: string;
  searchTerm?: string;
  isCTADisplayLogic?: boolean;
  doShowDocASAP?: boolean | string;
  choosePCP?: boolean;
  enableMapViewHideOption?: boolean;
  isGroupedLocationDrawer?: boolean;
  expandedProviderListView?: boolean;
  callToScheduleSize?: string;
  isSchedulingDataIndicator?: boolean;
};

export const Phone = ({
  headers = { correlationId: '' },
  id,
  phone,
  sectionType,
  locationForAnalytics,
  practitioner = {},
  popupContainer,
  providerType,
  indexForAnalytics,
  selectedFilters,
  searchTerm,
  isCTADisplayLogic = false,
  doShowDocASAP = false,
  choosePCP = false,
  enableMapViewHideOption,
  isGroupedLocationDrawer,
  expandedProviderListView,
  callToScheduleSize = 'md',
  isSchedulingDataIndicator = false,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const mobileOnlyScreen = useMediaQuery(mobileOnly);
  const [isOpen, setIsOpen] = useState(false);
  const { shouldShowSignIn } = usePortalSignInData();
  const effectiveDoShowDocASAP = Boolean(doShowDocASAP) && shouldShowSignIn;

  const phoneNumber: string = getFormattedAndValidatedPhoneNumber(
    phone,
    enableMapViewHideOption ?? false
  );
  const phoneNumberDataTestId: string = `phone-card-${sectionType}-tag`;
  const linkTrackLocation: string = phoneNumberDataTestId
    .slice(0, phoneNumberDataTestId.indexOf(`-${sectionType}-`))
    .concat(' tag');

  const handleOnClickCall = () => {
    if (phone) {
      adobeSearchResultClickEvent({
        filters: selectedFilters,
        linkName: linkTrackLocation,
        linkPosition: indexForAnalytics,
        linkLocation: locationForAnalytics,
        customAttributesBlock: {
          correlationId: headers.correlationId,
          providerId: formatProviderId(practitioner),
          providerType: convertProviderTypeToAdobeType(providerType),
        },
        term: searchTerm ?? '',
      });
    }
  };

  const isGEHAV2 = shouldUseGehaV2Styling();

  const phoneNumberWithIcon = useMemo(
    () => (
      <PhoneTag
        aria-describedby={practitioner?.providerName}
        data-auto-testid={phoneNumberDataTestId}
        data-testid={phoneNumberDataTestId}
        href={`tel:${phoneNumber}`}
        onClick={(e: SyntheticEvent) => e.stopPropagation()}
      >
        <IconSymbol
          aria-label="Phone number"
          data-auto-testid={`data-card-phone-icon-${sectionType}-${id}`}
          data-testid={`data-card-phone-icon-${sectionType}-${id}`}
          icon="phone"
          isScreenReadable
          size={mobileScreen ? 18 : 24}
          title="Phone number"
        />
        <ResponsiveText
          color="$core.color.brand.60"
          css={
            isGroupedLocationDrawer
              ? phoneTextStyleGroupLocationDrawer
              : phoneTextStyles(enableMapViewHideOption)
          }
          data-auto-testid={`data-card-phone-number-${sectionType}-${id}`}
          data-testid={`data-card-phone-number-${sectionType}-${id}`}
          fontWeight="bold"
          onClick={handleOnClickCall}
        >
          {phone ? phoneNumber : t('not_available')}
        </ResponsiveText>
      </PhoneTag>
    ),
    []
  );

  const phoneNumberWithoutIcon = useMemo(
    () => (
      <PhoneTag
        aria-describedby={practitioner?.providerName}
        data-auto-testid={phoneNumberDataTestId}
        data-testid={phoneNumberDataTestId}
        href={`tel:${phoneNumber}`}
        onClick={(e: SyntheticEvent) => e.stopPropagation()}
      >
        <ResponsiveText
          color="$core.color.brand.60"
          css={
            isGroupedLocationDrawer
              ? phoneTextStyleGroupLocationDrawer
              : phoneTextStyles(enableMapViewHideOption)
          }
          data-auto-testid={`data-card-phone-number-${sectionType}-${id}`}
          data-testid={`data-card-phone-number-${sectionType}-${id}`}
          fontWeight="bold"
          onClick={handleOnClickCall}
        >
          {phone ? phoneNumber : t('not_available')}
        </ResponsiveText>
      </PhoneTag>
    ),
    []
  );

  const phoneNumberWithCallToSchedule = useMemo(
    () => (
      <>
        {enableMapViewHideOption &&
        !effectiveDoShowDocASAP &&
        !isSchedulingDataIndicator ? (
          <>
            <Text
              color={isGEHAV2 ? '$core.color.brand.100' : ''}
              fontSize={callToScheduleSize}
            >
              {t('Call to schedule:')}
            </Text>
          </>
        ) : null}
        {!popupContainer && (
          <CallButton
            aria-label={`${phoneNumber} ${t('Call')} ${
              practitioner?.providerName
            }`}
            css={
              isGroupedLocationDrawer &&
              mobileOnlyScreen && {
                width: 'auto',
              }
            }
            cssProps={{
              enableMapViewHideOption,
              expandedProviderListView,
              doShowDocASAP: effectiveDoShowDocASAP,
              isGroupedLocationDrawer,
            }}
            data-testid={`phone-card-Provider-tag`}
            onClick={() => setIsOpen(true)}
          >
            {isGroupedLocationDrawer && mobileOnlyScreen ? null : (
              <IconSymbol
                color={callButtonIconColor(enableMapViewHideOption)}
                css={{ cursor: 'pointer' }}
                data-auto-testid={`data-card-call-icon-${sectionType}-${id}`}
                data-testid={`data-card-call-icon-${sectionType}-${id}`}
                icon="phone"
                size={mobileScreen ? 18 : 24}
              />
            )}
            <ResponsiveText
              color={callButtonIconColor(enableMapViewHideOption)}
              css={{
                ...phoneTextStyles(
                  enableMapViewHideOption,
                  isGroupedLocationDrawer
                ),
                padding: '4px 0!important',
              }}
              data-auto-testid={`data-card-call-cta-${sectionType}-${id}`}
              data-testid={`data-card-call-cta-${sectionType}-${id}`}
              fontWeight="bold"
            >
              {callText(
                enableMapViewHideOption,
                phone,
                phoneNumber,
                `${t('Call')}`
              )}
            </ResponsiveText>
          </CallButton>
        )}
      </>
    ),
    [callToScheduleSize]
  );

  return (
    <>
      {!isCTADisplayLogic && !popupContainer ? (
        phoneNumberWithIcon
      ) : !choosePCP ? (
        expandedProviderListView ? (
          <CallAndScheduleText cssProps={{ expandedProviderListView }}>
            {phoneNumberWithCallToSchedule}
          </CallAndScheduleText>
        ) : (
          phoneNumberWithCallToSchedule
        )
      ) : isGroupedLocationDrawer && mobileOnlyScreen ? (
        phoneNumberWithoutIcon
      ) : (
        phoneNumberWithIcon
      )}
      {isOpen ? (
        <CallModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          title="Snack Card Call Modal"
        >
          {phoneNumberWithIcon}
        </CallModal>
      ) : null}
    </>
  );
};
